import { useMsal } from "@azure/msal-react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import LiaiseWordmark from "../brand/LiaiseWordmark";
import { api } from "../api/client";

interface NavItem {
  to: string;
  label: string;
  roles: string[];
  badgeKey?: "pending_approvals";
}

// Nav consolidation: only Approvals + Admin at top level. Everything else lives inside Admin.
const NAV: NavItem[] = [
  { to: "/approvals", label: "Approvals", roles: ["Approver"], badgeKey: "pending_approvals" },
  { to: "/admin", label: "Admin", roles: ["Admin", "Payroll", "Finance", "HomeManager"] },
];

const CLAIMANT_SWA_URL =
  (import.meta.env.VITE_CLAIMANT_SWA_URL as string | undefined) ??
  "https://expenses.liaisegroup.com";

export default function AppShell({ children, roles }: { children: React.ReactNode; roles: string[] }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const { pathname } = useLocation();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });

  const visible = NAV.filter((n) => n.roles.some((r) => roles.includes(r)));

  const badgeFor = (n: NavItem): number => {
    if (!n.badgeKey || !me) return 0;
    return Number(me[n.badgeKey] ?? 0);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-liaise-navy text-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LiaiseWordmark className="h-7 w-auto" />
            <span className="text-white/40">|</span>
            <span className="font-medium">Expenses · Admin</span>
          </div>
          <div className="text-sm flex items-center gap-4">
            <a
              href={CLAIMANT_SWA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/90 hover:text-white border border-white/30 hover:border-white/50 rounded-full px-3 py-1 transition"
              title="Open the claimant app to submit your own expenses"
            >
              <span>My expenses</span>
            </a>
            <span className="text-white/80">{account?.name ?? account?.username}</span>
            <button
              onClick={() => instance.logoutRedirect()}
              className="text-white/80 hover:text-white underline-offset-4 hover:underline"
            >
              Sign out
            </button>
          </div>
        </div>
        {visible.length > 0 && (
          <nav className="border-t border-white/10">
            <div className="max-w-7xl mx-auto px-6 flex gap-6 text-sm">
              {visible.map((n) => {
                const active = n.to === "/admin"
                  ? pathname.startsWith("/admin") || pathname.startsWith("/payroll") || pathname.startsWith("/finance") || pathname.startsWith("/home")
                  : pathname.startsWith(n.to);
                const badge = badgeFor(n);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    className={`py-3 border-b-2 transition flex items-center gap-2 ${
                      active ? "border-white text-white" : "border-transparent text-white/70 hover:text-white"
                    }`}
                  >
                    <span>{n.label}</span>
                    {badge > 0 && (
                      <span className="bg-liaise-yellow text-liaise-navy text-xs font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                        {badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">{children}</main>

      <footer className="border-t border-slate-200 py-4 text-center text-sm text-slate-500">
        Your goals are our goals.
      </footer>
    </div>
  );
}
